type CredentialDescriptor = {
  readonly id: string;
  readonly type?: PublicKeyCredentialType;
  readonly transports?: readonly AuthenticatorTransport[];
};

type RequestOptions = {
  readonly challenge: string;
  readonly rpId?: string;
  readonly timeout?: number;
  readonly userVerification?: UserVerificationRequirement;
  readonly allowCredentials?: readonly CredentialDescriptor[];
};

type CreationOptions = {
  readonly challenge: string;
  readonly rp: PublicKeyCredentialRpEntity;
  readonly user: { readonly id: string; readonly name: string; readonly displayName: string };
  readonly pubKeyCredParams: readonly PublicKeyCredentialParameters[];
  readonly timeout?: number;
  readonly attestation?: AttestationConveyancePreference;
  readonly authenticatorSelection?: AuthenticatorSelectionCriteria;
  readonly excludeCredentials?: readonly CredentialDescriptor[];
};

type ErrorResponse = { readonly error?: string };
type LoginVerifyResponse = ErrorResponse & { readonly redirect?: string };

const LOGIN_OPTIONS_URL = '/auth/webauthn-login-options';
const LOGIN_VERIFY_URL = '/auth/webauthn-login-verify';
const REGISTER_OPTIONS_URL = '/auth/webauthn-register-options';
const REGISTER_VERIFY_URL = '/auth/webauthn-register-verify';

const base64urlToBuffer = (value: string): ArrayBuffer => {
  const padded = value
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(value.length / 4) * 4, '=');
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

const bufferToBase64url = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const toCredentialDescriptors = (descriptors: readonly CredentialDescriptor[] | undefined): PublicKeyCredentialDescriptor[] =>
  (descriptors ?? []).map((descriptor) => ({
    id: base64urlToBuffer(descriptor.id),
    type: descriptor.type ?? 'public-key',
    ...(descriptor.transports ? { transports: [...descriptor.transports] } : {}),
  }));

const isUserCancellation = (error: unknown): boolean => error instanceof DOMException && (error.name === 'AbortError' || error.name === 'NotAllowedError');

const postJson = async (url: string, body: unknown, csrf?: string): Promise<unknown> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  };
  if (csrf) {
    headers['X-CSRF-Token'] = csrf;
  }
  const resp = await fetch(url, {
    method: 'POST',
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    credentials: 'same-origin',
  });
  const data = (await resp.json().catch(() => ({}))) as ErrorResponse;
  if (!resp.ok) {
    throw new Error(data.error ?? 'Request failed. Please try again.');
  }
  return data;
};

const runLoginCeremony = async (): Promise<void> => {
  const options = (await postJson(LOGIN_OPTIONS_URL, undefined)) as RequestOptions;
  const credential = (await navigator.credentials.get({
    publicKey: {
      challenge: base64urlToBuffer(options.challenge),
      ...(options.rpId ? { rpId: options.rpId } : {}),
      ...(options.timeout ? { timeout: options.timeout } : {}),
      ...(options.userVerification ? { userVerification: options.userVerification } : {}),
      allowCredentials: toCredentialDescriptors(options.allowCredentials),
    },
  })) as PublicKeyCredential | null;

  if (!credential) {
    throw new Error('No passkey was provided.');
  }

  const response = credential.response as AuthenticatorAssertionResponse;
  const payload = {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      authenticatorData: bufferToBase64url(response.authenticatorData),
      signature: bufferToBase64url(response.signature),
      userHandle: response.userHandle ? bufferToBase64url(response.userHandle) : null,
    },
    clientExtensionResults: credential.getClientExtensionResults(),
  };

  const rememberInput = document.getElementById('remember_me');
  const remember = rememberInput instanceof HTMLInputElement ? rememberInput.checked : false;

  const result = (await postJson(LOGIN_VERIFY_URL, { credential: payload, remember })) as LoginVerifyResponse;
  if (!result.redirect) {
    throw new Error('Sign-in did not complete. Please try again.');
  }
  window.location.assign(result.redirect);
};

const runRegisterCeremony = async (csrf: string, label?: string): Promise<void> => {
  const options = (await postJson(REGISTER_OPTIONS_URL, undefined, csrf)) as CreationOptions;
  const credential = (await navigator.credentials.create({
    publicKey: {
      challenge: base64urlToBuffer(options.challenge),
      rp: options.rp,
      user: {
        id: base64urlToBuffer(options.user.id),
        name: options.user.name,
        displayName: options.user.displayName,
      },
      pubKeyCredParams: [...options.pubKeyCredParams],
      ...(options.timeout ? { timeout: options.timeout } : {}),
      ...(options.attestation ? { attestation: options.attestation } : {}),
      ...(options.authenticatorSelection ? { authenticatorSelection: options.authenticatorSelection } : {}),
      excludeCredentials: toCredentialDescriptors(options.excludeCredentials),
    },
  })) as PublicKeyCredential | null;

  if (!credential) {
    throw new Error('No passkey was created.');
  }

  const response = credential.response as AuthenticatorAttestationResponse;
  const payload = {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      attestationObject: bufferToBase64url(response.attestationObject),
      transports: response.getTransports?.() ?? [],
    },
    clientExtensionResults: credential.getClientExtensionResults(),
  };

  await postJson(REGISTER_VERIFY_URL, { credential: payload, label: label ?? '' }, csrf);
  window.location.reload();
};

const withBusyButton = async (button: HTMLButtonElement, task: () => Promise<void>): Promise<void> => {
  button.disabled = true;
  try {
    await task();
  } finally {
    button.disabled = false;
  }
};

const setStatus = (el: HTMLElement | null, message: string, isError: boolean): void => {
  if (!el) {
    return;
  }
  el.textContent = message;
  el.classList.toggle('auth-error', isError);
  el.hidden = message === '';
};

const initSignIn = (): void => {
  const button = document.getElementById('passkey-signin');
  if (!(button instanceof HTMLButtonElement)) {
    return;
  }
  button.hidden = false;
  const status = document.getElementById('passkey-signin-status');
  button.addEventListener('click', () => {
    setStatus(status, '', false);
    void withBusyButton(button, async () => {
      try {
        await runLoginCeremony();
      } catch (error) {
        if (isUserCancellation(error)) {
          return;
        }
        setStatus(status, error instanceof Error ? error.message : 'Could not sign in with a passkey.', true);
      }
    });
  });
};

const initSetup = (): void => {
  const button = document.getElementById('passkey-add');
  const manager = document.getElementById('passkey-manager');
  if (!(button instanceof HTMLButtonElement) || !manager) {
    return;
  }
  const status = document.getElementById('passkey-add-status');
  const csrf = manager.dataset.csrf;
  if (!csrf) {
    return;
  }
  button.addEventListener('click', () => {
    setStatus(status, '', false);
    const labelInput = document.getElementById('passkey-label');
    const label = labelInput instanceof HTMLInputElement ? labelInput.value.trim() : undefined;
    void withBusyButton(button, async () => {
      try {
        await runRegisterCeremony(csrf, label);
      } catch (error) {
        if (isUserCancellation(error)) {
          return;
        }
        setStatus(status, error instanceof Error ? error.message : 'Could not add a passkey.', true);
      }
    });
  });
};

document.addEventListener('DOMContentLoaded', () => {
  if (typeof window.PublicKeyCredential === 'undefined') {
    document.getElementById('passkey-unsupported')?.removeAttribute('hidden');
    return;
  }
  initSignIn();
  initSetup();
});
